import React from 'react';
import { Text, View } from 'react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import moment from 'moment';

export interface TableColumn {
  key: string;
  title: string;
  width?: number;
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
}

/**
 * Generate table columns from data schema
 */
export const generateColumnsFromSchema = (dataSchema: any, sampleData?: any[]): TableColumn[] => {
  if (!dataSchema || !Array.isArray(dataSchema)) {
    return [];
  }

  return dataSchema.map((field: any) => {
    const column: TableColumn = {
      key: field.key || field.name || field.id,
      title: field.title || field.label || field.name || field.key,
      width: getOptimalColumnWidth(field, sampleData),
      sortable: true,
      filterable: true,
      align: getColumnAlignment(field.type),
      render: getColumnRenderer(field.type, field)
    };

    return column;
  });
};

/**
 * Generate columns from sample data
 */
export const generateColumnsFromData = (data: any[]): TableColumn[] => {
  if (!data || data.length === 0) {
    return [];
  }

  const sampleRow = data[0];
  const keys = Object.keys(sampleRow);

  return keys.map(key => ({
    key,
    title: formatColumnTitle(key),
    width: getOptimalWidthFromData(data, key),
    sortable: true,
    filterable: true,
    align: getAlignmentFromValue(sampleRow[key]),
    render: getRendererFromValue(sampleRow[key])
  }));
};

/**
 * Get optimal column width based on field type and sample data
 */
const getOptimalColumnWidth = (field: any, sampleData?: any[]): number => {
  const baseWidths = {
    string: 150,
    number: 100,
    date: 120,
    boolean: 80,
    email: 200,
    phone: 140,
    url: 180,
    text: 200,
    default: 150
  };

  let baseWidth = baseWidths[field.type] || baseWidths.default;

  if (sampleData && sampleData.length > 0) {
    const maxLength = Math.max(
      ...sampleData.map(row => {
        const value = row[field.key];
        return value ? value.toString().length : 0;
      })
    );

    // Adjust width based on content length
    baseWidth = Math.max(baseWidth, Math.min(maxLength * 8 + 20, 300));
  }

  return baseWidth;
};

/**
 * Get optimal width from actual data
 */
const getOptimalWidthFromData = (data: any[], key: string): number => {
  const maxLength = Math.max(
    ...data.map(row => {
      const value = row[key];
      return value ? value.toString().length : 0;
    })
  );

  return Math.max(100, Math.min(maxLength * 8 + 20, 300));
};

/**
 * Get column alignment based on field type
 */
const getColumnAlignment = (type: string): 'left' | 'center' | 'right' => {
  switch (type) {
    case 'number':
    case 'currency':
    case 'percentage':
      return 'right';
    case 'boolean':
    case 'status':
      return 'center';
    default:
      return 'left';
  }
};

/**
 * Get alignment from value type
 */
const getAlignmentFromValue = (value: any): 'left' | 'center' | 'right' => {
  if (typeof value === 'number') return 'right';
  if (typeof value === 'boolean') return 'center';
  return 'left';
};

/**
 * Get column renderer based on field type
 */
const getColumnRenderer = (type: string, field: any) => {
  return (value: any, row: any) => {
    return getRendererFromType(type, value, field);
  };
};

/**
 * Get renderer from value type
 */
const getRendererFromValue = (value: any) => {
  if (typeof value === 'boolean') {
    return (val: boolean) => getBooleanRenderer(val);
  }
  if (typeof value === 'number') {
    return (val: number) => getNumberRenderer(val);
  }
  if (typeof value === 'string' && isDateString(value)) {
    return (val: string) => getDateRenderer(val);
  }
  if (typeof value === 'string' && isEmail(value)) {
    return (val: string) => getEmailRenderer(val);
  }
  if (typeof value === 'string' && isUrl(value)) {
    return (val: string) => getUrlRenderer(val);
  }
  return (val: any) => getDefaultRenderer(val);
};

/**
 * Get renderer from field type
 */
const getRendererFromType = (type: string, value: any, field: any) => {
  switch (type) {
    case 'boolean':
      return getBooleanRenderer(value);
    case 'number':
    case 'currency':
    case 'percentage':
      return getNumberRenderer(value, type);
    case 'date':
    case 'datetime':
      return getDateRenderer(value);
    case 'email':
      return getEmailRenderer(value);
    case 'url':
      return getUrlRenderer(value);
    case 'status':
      return getStatusRenderer(value);
    case 'tags':
      return getTagsRenderer(value);
    default:
      return getDefaultRenderer(value);
  }
};

/**
 * Boolean renderer
 */
const getBooleanRenderer = (value: boolean) => {
  const theme = themeSelector();
  return (
    <View style={{
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
      backgroundColor: value ? '#d4edda' : '#f8d7da',
      alignSelf: 'center'
    }}>
      <Text style={{
        fontSize: 12,
        fontWeight: '600',
        color: value ? '#155724' : '#721c24'
      }}>
        {value ? 'Yes' : 'No'}
      </Text>
    </View>
  );
};

/**
 * Number renderer
 */
const getNumberRenderer = (value: number, type?: string) => {
  if (value === null || value === undefined) return <Text>-</Text>;

  let formattedValue = value.toString();

  if (type === 'currency') {
    formattedValue = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  } else if (type === 'percentage') {
    formattedValue = `${(value * 100).toFixed(1)}%`;
  } else if (typeof value === 'number') {
    formattedValue = new Intl.NumberFormat('en-US').format(value);
  }

  return (
    <Text
      style={{ textAlign: 'right', fontFamily: 'monospace', width: '100%' }}
      numberOfLines={1}
      ellipsizeMode="tail"
    >
      {formattedValue}
    </Text>
  );
};

/**
 * Date renderer
 */
const getDateRenderer = (value: string | Date) => {
  if (!value) return <Text>-</Text>;

  const date = moment(value);
  if (!date.isValid()) return <Text>-</Text>;

  return (
    <Text
      style={{ fontSize: 12, width: '100%' }}
      numberOfLines={1}
      ellipsizeMode="tail"
    >
      {date.format('MMM DD, YYYY')}
    </Text>
  );
};

/**
 * Email renderer
 */
const getEmailRenderer = (value: string) => {
  if (!value) return <Text>-</Text>;

  return (
    <Text
      style={{
        color: '#007bff',
        textDecorationLine: 'underline',
        fontSize: 12,
        width: '100%'
      }}
      numberOfLines={1}
      ellipsizeMode="tail"
    >
      {value}
    </Text>
  );
};

/**
 * URL renderer
 */
const getUrlRenderer = (value: string) => {
  if (!value) return <Text>-</Text>;

  return (
    <Text
      style={{
        color: '#007bff',
        textDecorationLine: 'underline',
        fontSize: 12,
        width: '100%'
      }}
      numberOfLines={1}
      ellipsizeMode="tail"
    >
      {value}
    </Text>
  );
};

/**
 * Status renderer
 */
const getStatusRenderer = (value: string) => {
  const statusColors = {
    active: '#28a745',
    inactive: '#6c757d',
    pending: '#ffc107',
    completed: '#28a745',
    failed: '#dc3545',
    cancelled: '#6c757d'
  };

  const color = statusColors[value?.toLowerCase()] || '#6c757d';

  return (
    <View style={{
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
      backgroundColor: `${color}20`,
      alignSelf: 'center'
    }}>
      <Text style={{
        fontSize: 12,
        fontWeight: '600',
        color: color,
        textTransform: 'capitalize'
      }}>
        {value || 'Unknown'}
      </Text>
    </View>
  );
};

/**
 * Tags renderer
 */
const getTagsRenderer = (value: string[] | string) => {
  if (!value) return <Text>-</Text>;

  const tags = Array.isArray(value) ? value : [value];

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
      {tags.slice(0, 3).map((tag, index) => (
        <View key={index} style={{
          paddingHorizontal: 6,
          paddingVertical: 2,
          borderRadius: 8,
          backgroundColor: '#e9ecef'
        }}>
          <Text style={{ fontSize: 10, color: '#495057' }}>
            {tag}
          </Text>
        </View>
      ))}
      {tags.length > 3 && (
        <Text style={{ fontSize: 10, color: '#6c757d' }}>
          +{tags.length - 3} more
        </Text>
      )}
    </View>
  );
};

/**
 * Default renderer
 */
const getDefaultRenderer = (value: any) => {
  if (value === null || value === undefined) return <Text>-</Text>;

  const stringValue = typeof value === 'object' ? JSON.stringify(value) : value.toString();

  return (
    <Text
      style={{ fontSize: 12, width: '100%' }}
      numberOfLines={1}
      ellipsizeMode="tail"
    >
      {stringValue}
    </Text>
  );
};

/**
 * Format column title
 */
const formatColumnTitle = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

/**
 * Utility functions
 */
const isDateString = (value: string): boolean => {
  return !isNaN(Date.parse(value)) && /^\d{4}-\d{2}-\d{2}/.test(value);
};

const isEmail = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

const isUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};